"use client";

/* eslint-disable no-nested-ternary */
import { getConvertedJpeg } from "api/get-converted-jpeg.api";
import { imageType } from "api/interfaces/home/home.type";
import classnames from "classnames";
import SliderV2 from "component/design-system/carousel/SliderV2";
import useScreenSize, { useCustomScreenSize } from "hoc/useScreenSize";
import WebpContext from "hooks-contexts/WebpContext";
import { RootState } from "interfaces/generics.type";
import styles from "modules/home/home.module.scss";
import { DEFAULT_CATEGORY_BANNER } from "modules/site-structure/common/make-model-year-utils";
import { STATIC_PAGES } from "modules/static-pages/static-page-constants";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import qs from "query-string";
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { getAbsoluteImagePath } from "util/helper/getAbsoluteImagePath";
import { getCleanCategoryObj } from "util/helper/getCleanCategoryObj";
import { isEmpty } from "util/lodash/isEmpty";
import { omit } from "util/lodash/omit";
import PASentry from "util/pa-sentry";
import { getHrefAndPath } from "util/url/getHrefAndPath";
import RenderBannerItem from "./RenderBannerItem";

interface IProps {
  bannersWebp?: imageType,
  baseImageUrl?: string,
}

function HomepageBannerComponent(props: IProps) {
  const { bannersWebp: newBanners, baseImageUrl = process.env.NEXT_PUBLIC_CMS } = props;
  if (!newBanners || newBanners?.length === 0) {
    return null;
  }
  const { canUseWebp } = useContext(WebpContext);
  const initialBanners = formatBanners(newBanners);
  const [banners, setBanners] = useState(initialBanners);
  const router = useRouter();

  const selectedVehicle = useSelector((state: RootState) => state.garage.selectedVehicle);
  const [isMobile] = useScreenSize();
  const isExtraLarge = useCustomScreenSize({ minWidth: 1400 });
  const isLarge = useCustomScreenSize({ minWidth: 1025 });
  const isTablet = useCustomScreenSize({ minWidth: 768 });
  const isLargeMobile = useCustomScreenSize({ minWidth: 425 });

  function formatBanners(bannersList: any) {
    return bannersList.map((banner: any) => ({
      ...banner,
      small: `${baseImageUrl}/${banner.small}`,
      medium: `${baseImageUrl}/${banner.medium}`,
      large: `${baseImageUrl}/${banner.large}`,
      extraLarge: `${baseImageUrl}/${banner.extraLarge}`,
    }));
  }

  function getCanonicalLink(banner: any) {
    const urlObject = qs.parseUrl(banner.url);
    const { query, url } = urlObject;
    const { href, asPath } = getHrefAndPath({
      query, asPath: url,
    });
    return { href, asPath };
  }

  function getBannerUrl(banner: any) {
    if (!isEmpty(banner.url)) {
      if (banner.url.indexOf("https://") === 0) {
        // https://stackoverflow.com/a/58875962/2980047
        return { href: banner.url, asPath: banner.url, externalLink: true };
      } if (STATIC_PAGES.includes(banner.url)) {
        if (!banner.url || banner.url === "undefined") {
          PASentry.logException(new Error("aspath is undefined"), null, { href: banner.url, bannerUrl: true, bannerType: typeof banner.url });
        }
        return { href: "/[pathname]", asPath: banner.url };
      }
      return getCanonicalLink(banner);
    }
    return { href: "/", asPath: "/" };
  }

  let bannerKey = "large";
  if (isExtraLarge) {
    bannerKey = "extraLarge";
  } else if (isLarge) {
    bannerKey = "large";
  } else if (isTablet) {
    bannerKey = "medium";
  } else if (isLargeMobile) {
    bannerKey = "small";
  } else if (isMobile) {
    bannerKey = "small";
  }

  function handleClickBanner(banner) {
    const urlObject = qs.parseUrl(banner.url);
    const { query, url } = urlObject;

    if (url.includes("/i/")) {
      window.open(url, "_self");
      return;
    }

    const {
      tier0, tier1, tier2, tier3,
    } = query;

    const cleanCategoryObj = getCleanCategoryObj({
      tier0,
      tier1,
      tier2,
      tier3,
    });
    const buildQueryParams = omit(query, ["tier0", "tier1", "tier2", "tier3"]);
    import("component/categories-viewer/handler/CategoryViewItemhandler")
      .then((module) => {
        module.categoryViewItemhandler(router, cleanCategoryObj, selectedVehicle, null, { buildQueryParams, url });
      });
  }

  useEffect(() => {
    const updatedBanners = formatBanners(newBanners);

    if (canUseWebp) {
      setBanners(updatedBanners);
      return;
    }

    const bannersPromiseArr = updatedBanners.map((banner) => {
      const bannerUrl = banner[bannerKey] || DEFAULT_CATEGORY_BANNER;
      const absoluteSrc = getAbsoluteImagePath(bannerUrl);
      return getConvertedJpeg(absoluteSrc);
    });
    Promise.all(bannersPromiseArr).then((bannerValues) => {
      setBanners(updatedBanners.map((banner, idx) => ({
        ...banner,
        [bannerKey]: `data:image/jpeg;base64,${bannerValues[idx]}`,
      })));
    });
  }, [newBanners, canUseWebp]);

  function RenderBanners() {
    return banners.map((banner: any, idx) => {
      const bannerUrl = banner[bannerKey] || DEFAULT_CATEGORY_BANNER;
      const {
        href, asPath, externalLink,
      } = getBannerUrl(banner);
      if (bannerUrl.split(".").pop() === "mp4") {
        return (
          (
            <Link
              key={idx}
              href={href}
              as={asPath}
              target={externalLink ? "_blank" : "_self"}
            >

              <div key={idx}>
                <video
                  key={bannerUrl}
                  preload="none"
                  playsInline
                  autoPlay
                  loop
                  muted
                  style={{ objectFit: "fill" }}
                  className={classnames(styles.banner, {
                    pointer: !isEmpty(banner.url),
                  })}
                >
                  <source src={`${baseImageUrl}/${bannerUrl}`} type="video/mp4" />
                </video>
              </div>

            </Link>
          )
        );
      }

      if (selectedVehicle) {
        return (
          <RenderBannerItem key={banner.url} banner={banner} idx={idx} onClick={handleClickBanner} baseImageUrl={baseImageUrl} />
        );
      }

      return (
        (
          <Link
            key={idx}
            href={href}
            as={asPath}
            className={styles.banner_link}
            target={externalLink ? "_blank" : "_self"}
          >

            <RenderBannerItem banner={banner} idx={idx} baseImageUrl={baseImageUrl} />

          </Link>
        )
      );
    });
  }

  return (
    <div className={styles.bannerWrap}>

      <div className={styles.bannerContainer}>

        <Head>
          <link rel="preload" as="image" href={banners[0].extraLarge} media="(min-width: 1400px)" />
          <link rel="preload" as="image" href={banners[0].large} media="(min-width: 1024px) and (max-width: 1399px)" />
          <link rel="preload" as="image" href={banners[0].medium} media="(min-width: 768px) and (max-width: 1023px)" />
          <link rel="preload" fetchPriority="high" as="image" href={banners[0].small} media="(max-width: 767px)" />
        </Head>

        <HideIf show={banners.length === 1}>
          <RenderBanners />
        </HideIf>
        <HideIf show={banners.length > 1}>
          <SliderV2
            ulClassName={styles.home_banner_ul}
            dotsClassName={styles.home_banner_dots}
            activeDotClassName={styles.home_banner_dot_active}
            autoplay={!isMobile}
            autoplaySpeed={5000}
            showArrows={false}
            lazyLoad
            className={styles.slider_container}
          >
            {RenderBanners()}
          </SliderV2>
        </HideIf>

      </div>
    </div>

  );
}

export default HomepageBannerComponent;
